<template>
  <div class="p-2 advanced-table-wrapper">
    <div
      v-if="isLoading"
      class="loading"
    >
      <img
        src="@/assets/images/loading.gif"
        alt="Loading"
      >
    </div>
    <div class="table-area">
      <div class="actions-row">
        <slot name="table-top-actions" />
        <div
          v-if="visibleRows.length !== 0 && showPagination"
          class="pagination flex items-center gap-2"
        >
          <Pagination
            :page-count="table.getPageCount()"
            :page-range="5"
            :click-handler="clickHandler"
            :first-last-button="true"
            container-class="button-group"
            prev-link-class="border left rounded p-1"
            page-link-class="border rounded p-1"
            next-link-class="border right rounded p-1"
            :margin-pages="0"
            :value="table.getState().pagination.pageIndex"
          />
          <div class="per-page-wrapper">
            <span class="per-page-text">Her Sayfada</span>
            <SimpleSelect
              :simple-select-items="pageSizes"
              :selected-key="table.getState().pagination.pageSize"
              class="simpleSelect"
              @btn-item-click="table.setPageSize(Number($event))"
            />
          </div>
        </div>
      </div>
      <div
        class="table-wrapper"
        :style="[{ ...columnSizeVars() }]"
      >
        <table class="advanced-table">
          <thead>
            <tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :class="[{ pinned: header.column.getIsPinned() }]"
                :colSpan="header.colSpan"
                :style="[{ ...getCommonPinningStyles(header) }]"
              >
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
                <div
                  v-if="header.column.columnDef.enableResizing"
                  class="resizer"
                  :class="[{ 'is-resizing': header.column.getIsResizing() }]"
                  @dblclick="() => header.column.resetSize()"
                  @mousedown="header.getResizeHandler()?.($event)"
                  @touchstart="header.getResizeHandler()?.($event)"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.id"
              :class="[
                { selected: row.getIsSelected() },
                { disabled: !row.getCanSelect() },
                { 'status-pending': row.original?.status === 'Pending' },
              ]"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :style="{ ...getCommonPinningStyles(cell) }"
              >
                <FlexRender
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="visibleRows.length === 0"
          class="empty-table"
        >
          <img
            src="@/assets/images/empty-search.png"
            alt="Ürün bulunamadı"
          >
          <span class="title">Seçtiğiniz kriterlere uygun ürün bulunmuyor.</span>
          <span class="description">Girdiğiniz bilgileri kontrol edebilirsiniz.</span>
          <UIButton
            primary
            @click="goToAddNewProduct"
          >
            <img src="@/assets/images/icons/plus-white.svg">
            Yeni Ürün Ekle
          </UIButton>
        </div>
      </div>
      <div
        v-if="visibleRows.length !== 0 && showPagination"
        class="pagination bottom-pagination"
      >
        <Pagination
          :page-count="table.getPageCount()"
          :page-range="5"
          :click-handler="clickHandler"
          :first-last-button="true"
          container-class="button-group"
          prev-link-class="border left rounded p-1"
          page-link-class="border rounded p-1"
          next-link-class="border right rounded p-1"
          :margin-pages="0"
          :value="table.getState().pagination.pageIndex"
        />
        <div class="per-page-wrapper">
          <span class="per-page-text">Her Sayfada</span>
          <SimpleSelect
            :simple-select-items="pageSizes"
            :selected-key="table.getState().pagination.pageSize"
            open-menu-position="top-left"
            @btn-item-click="table.setPageSize(Number($event))"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { Button as UIButton, SimpleSelect } from '@n11/so-ui-components';
import { ref, computed, onMounted, watch } from 'vue';
import Pagination from './Pagination';
import useService from './useService';

export default {
  components: {
    FlexRender,
    UIButton,
    Pagination,
    SimpleSelect,
  },
  props: {
    payload: {
      type: Object,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['row-selected'],
  setup(props, { emit }) {
    const INITIAL_PAGE_INDEX = 0;
    const INITIAL_PAGE_SIZE = 10;

    const pageSizes = {
      10: '10 Ürün',
      25: '25 Ürün',
      50: '50 Ürün',
      100: '100 Ürün',
    };
    const table = ref(null);
    const pagination = ref({
      pageIndex: INITIAL_PAGE_INDEX,
      pageSize: INITIAL_PAGE_SIZE,
    });
    const rawPayload = ref({ ...props.payload });
    const columnVisibility = ref({});
    const columnOrder = ref([]);
    const customizedColumns = ref([]);
    const { columns } = props;
    const { data, isLoading, pageCount, nextCursor, prevCursor } = useService(
      pagination,
      rawPayload,
    );
    let toggleCustomizedColumns = false;
    let checkboxDisabledStatus = ref([]);
    const selectedStatuses = ref([]);

    watch(
      () => props.payload,
      (newVal) => {
        rawPayload.value = { ...newVal };
      },
    );

    watch(
      () => props.payload.tabParam,
      (newTabParam) => {
        checkboxDisabledStatus.value =
          newTabParam === 'all_products'
            ? ['InApproval', 'InCatalogApproval']
            : ['InCatalogApproval'];
      },
    );

    function updatePayload({
      after = null,
      before = null,
      jumpPage = null,
      goLast = false,
    }) {
      rawPayload.value = {
        ...rawPayload.value,
        after,
        before,
        jumpPage,
        goLast,
      };
    }

    const goToNextPage = () => {
      updatePayload({ after: nextCursor.value });
    };

    const goToPreviousPage = () => {
      updatePayload({ before: prevCursor.value });
    };

    const goToJumpPage = (pageDiff) => {
      updatePayload({
        after: pageDiff > 0 ? nextCursor.value : null,
        before: pageDiff < 0 ? prevCursor.value : null,
        jumpPage: Math.abs(pageDiff),
      });
    };

    const goToLastPage = () => {
      updatePayload({ goLast: true });
    };

    const goFirstPage = () => {
      updatePayload({});
    };

    const goToAddNewProduct = () => {
      window.open('/selleroffice/v2/product/create/single', '_blank');
    };
    function setPagination({ pageIndex, pageSize }) {
      const prevPageIndex = pagination.value.pageIndex;
      const totalPages = table.value.getPageCount();
      const pageIndexDiff = pageIndex - prevPageIndex;

      if (pageIndex === 0) {
        goFirstPage();
      } else if (pageIndex === totalPages - 1) {
        goToLastPage();
      } else if (pageIndexDiff === 1) {
        goToNextPage();
      } else if (pageIndexDiff === -1) {
        goToPreviousPage();
      } else if (Math.abs(pageIndexDiff) > 1) {
        goToJumpPage(pageIndexDiff);
      }

      pagination.value.pageIndex = pageIndex;
      pagination.value.pageSize = pageSize;

      return { pageIndex, pageSize };
    }

    const setCustomizedColumns = () => {
      if (!customizedColumns.value.length) {
        const customizedColumnsObject = [];
        columns
          .filter((c) => c.columnDef.meta.sortable)
          .forEach((c, i) => {
            const col = {
              key: c.accessorKey,
              order: c.columnDef.meta.order || i,
              title: c.columnDef.meta.title,
              isDetachable: c.columnDef.meta.detachable,
              isSortable: c.columnDef.meta.sortable,
              isVisible: c.columnDef.meta.visible,
            };
            customizedColumnsObject.push(col);
          });
        customizedColumns.value = customizedColumnsObject.sort(
          (a, b) => a.order - b.order,
        );
      }
      const colConfigFromLS = window.localStorage.getItem('columnConfig');
      if (colConfigFromLS) {
        const config = JSON.parse(colConfigFromLS);
        if (config.length !== 0) {
          customizedColumns.value.forEach((c) => {
            const selectedConfig = config.find((conf) => conf.key === c.key);
            if (selectedConfig) {
              c.order = selectedConfig.order;
              c.isVisible = selectedConfig.isVisible;
            }
          });
          customizedColumns.value.sort((a, b) => a.order - b.order);
          columnOrder.value = customizedColumns.value.map((cc) => cc.key);
        }
      }
      const cvConfig = {};
      customizedColumns.value.forEach((c) => {
        cvConfig[c.key] = c.isVisible;
      });
      columnVisibility.value = cvConfig;
    };

    table.value = useVueTable({
      get data() {
        return data.value ?? [];
      },
      get pageCount() {
        return pageCount.value ?? -1;
      },
      get columns() {
        return columns;
      },
      enableColumnPinning: true,
      getRowId: (row) => row.id,
      defaultColumn: {
        minSize: 60,
        maxSize: 800,
      },
      // debugAll: true,
      enableRowSelection: (row) =>
        !checkboxDisabledStatus.value.includes(row.original.status),
      columnResizeMode: 'onChange',
      initialState: {
        columnOrder: columnOrder.value,
        columnPinning: {
          left: ['select-col', 'productName'],
          right: ['actions'],
        },
      },
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      onPaginationChange: (updater) => {
        if (typeof updater === 'function') {
          setPagination(
            updater({
              pageIndex: pagination.value.pageIndex,
              pageSize: pagination.value.pageSize,
            }),
          );
        } else {
          setPagination(updater);
        }
      },
      rowCount: props.products?.totalElements,
      state: {
        get columnOrder() {
          return columnOrder.value;
        },
        pagination: pagination.value,
        get columnVisibility() {
          return columnVisibility.value;
        },
      },
      onColumnOrderChange: (order) => {
        columnOrder.value =
          order instanceof Function ? order(columnOrder.value) : order;
      },
      meta: {
        onCheckboxChange: (row, allChecked) => {
          if (allChecked !== 'singleChecked') {
            if (allChecked === 'selectAll') {
              row.forEach((item) => {
                if (item.getIsSelected()) {
                  selectedStatuses.value.push(item.original.status);
                }
              });
            } else {
              selectedStatuses.value = [];
            }
          } else {
            if (row.getIsSelected()) {
              selectedStatuses.value.push(row.original.status);
            } else {
              const index = selectedStatuses.value.indexOf(row.original.status);
              if (index !== -1) {
                selectedStatuses.value.splice(index, 1);
              }
            }
          }
          emit('row-selected', selectedStatuses.value);
        },
        toggleColumnVisibility: (column) => {
          const selectedColumn = customizedColumns.value.find(
            (c) => c.key === column.key,
          );
          customizedColumns.value.find((c) => c.key === column.key).isVisible =
            !selectedColumn.isVisible;
          toggleCustomizedColumns = customizedColumns.value.every(
            (c) => (c.isDetachable && c.isVisible) || !c.isDetachable,
          );
        },
        setStatusOfCheckboxLocalStorage: () => {
          const visibleStatus = getColumnConfigObjectFromLS();
          visibleStatus.forEach((status) => {
            customizedColumns.value.find(
              (column) => column.key === status.key,
            ).isVisible = status.isVisible;
          });
          toggleCustomizedColumns = getAllColumnConfigIsVisibleFromLS();
        },
        resetAllColumnVisibility: (event) => {
          if (event) event.preventDefault();

          customizedColumns.value.map((c) => {
            if (c.isDetachable) c.isVisible = false;
          });

          toggleCustomizedColumns = false;
        },
        getIsAllColumnsVisible: () => {
          return toggleCustomizedColumns;
        },
        toggleAllColumnsVisibility: () => {
          customizedColumns.value.map((c) => {
            if (c.isDetachable) c.isVisible = !toggleCustomizedColumns;
          });
          toggleCustomizedColumns = !toggleCustomizedColumns;
        },
        orderColumns: (event) => {
          if (event.oldIndex === event.newIndex) return;

          const clamp = (value, max) => Math.max(0, Math.min(max, value));
          const moveItemInArray = (array, fromIndex, toIndex) => {
            const from = clamp(fromIndex, array.length - 1);
            const to = clamp(toIndex, array.length - 1);

            if (from === to) return;

            const target = array[from];
            const delta = to < from ? -1 : 1;

            for (let i = from; i !== to; i += delta) {
              array[i] = array[i + delta];
            }

            array[to] = target;

            array.forEach((e, i) => (e.order = i));

            return array;
          };
          const ordered = moveItemInArray(
            customizedColumns.value,
            event.oldIndex,
            event.newIndex,
          );
          customizedColumns.value = ordered;
        },
        submitCustomizedColumns: () => submitCustomizedColumns(),
      },
    });

    const clickHandler = (pageNum) => {
      table.value.setPageIndex(pageNum);
    };

    const getCommonPinningStyles = (column) => {
      const isPinned = column.column.getIsPinned();
      const isLastLeftPinnedColumn =
        isPinned === 'left' && column.column.getIsLastColumn('left');
      const isFirstRightPinnedColumn =
        isPinned === 'right' && column.column.getIsFirstColumn('right');

      return {
        boxShadow: isLastLeftPinnedColumn
          ? '-4px 0 4px -4px gray inset'
          : isFirstRightPinnedColumn
            ? '4px 0 4px -4px gray inset'
            : undefined,
        left:
          isPinned === 'left'
            ? `${column.column.getStart('left')}px`
            : undefined,
        right:
          isPinned === 'right'
            ? `${column.column.getAfter('right')}px`
            : undefined,
        opacity: isPinned ? 0.95 : 1,
        position: isPinned ? 'sticky' : 'relative',
        width: column.column.getSize(),
        zIndex: isPinned ? 1 : 'unset',
        minWidth: `calc(var(--col-${column.column.id}-size) * 1px)`,
        maxWidth:
          !isPinned && column.column.id !== 'status'
            ? `calc(var(--col-${column.column.id}-size) * 1px)`
            : 'auto',
      };
    };

    const columnSizeVars = () => {
      const headers = table.value.getFlatHeaders();
      const colSizes = {};

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
    };

    const getSelectedRows = () => table.value.getState().rowSelection;
    const getAllColumnConfigIsVisibleFromLS = () => {
      const config = getColumnConfigObjectFromLS();
      const visible = config.every((conf) => conf.isVisible);
      return visible;
    };
    const getColumnConfigObjectFromLS = () => {
      const columnConfigFromLS = window.localStorage.getItem('columnConfig');
      const config = JSON.parse(columnConfigFromLS);
      return config;
    };

    const submitCustomizedColumns = () => {
      const storedConfigForColumns = [];
      customizedColumns.value.forEach((c) => {
        const colConfig = {
          key: c.key,
          order: c.order,
          isVisible: c.isVisible,
        };
        storedConfigForColumns.push(colConfig);
      });
      window.localStorage.setItem(
        'columnConfig',
        JSON.stringify(storedConfigForColumns),
      );
      columnOrder.value = customizedColumns.value.map((cc) => cc.key);

      const cvConfig = {};
      customizedColumns.value.forEach((c) => {
        cvConfig[c.key] = c.isVisible;
      });
      toggleCustomizedColumns = getAllColumnConfigIsVisibleFromLS();
      columnVisibility.value = cvConfig;
    };

    const visibleRows = computed(() => {
      if (table.value) {
        return table.value.getRowModel().rows || [];
      }
      return [];
    });

    onMounted(() => {
      setCustomizedColumns();
      submitCustomizedColumns();
      toggleCustomizedColumns = getAllColumnConfigIsVisibleFromLS();
    });

    return {
      table,
      isLoading,
      columnSizeVars,
      getCommonPinningStyles,
      clickHandler,
      getSelectedRows,
      goToAddNewProduct,
      customizedColumns,
      visibleRows,
      pageSizes,
      pagination,
      columnVisibility,
      columnOrder,
      selectedStatuses,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.advanced-table-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: 'Open Sans', sans-serif;
  gap: 24px;

  .loading {
    position: absolute;
    z-index: 1;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    // prettier-ignore
    background-color: rgba(0, 0, 0, 0.07);
  }

  .table-area {
    position: relative;
    z-index: 0;
  }
}

.table-wrapper {
  position: relative;
  overflow: scroll auto;
  width: 100%;
  max-width: 1328px;
  max-height: 90dvh;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-collapse: separate;
  border-spacing: 0;

  .resizer {
    position: absolute;
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    background: rgba($colorN50, 0.5);
    cursor: col-resize;
    touch-action: none;
    transition: opacity 0.25s ease-in-out;
    user-select: none;
  }

  .resizer.is-resizing {
    background: $colorD50;
    opacity: 1;
  }

  @media (hover: hover) {
    .resizer {
      opacity: 0;
    }

    *:hover > .resizer {
      opacity: 1;
    }
  }

  .empty-table {
    position: sticky;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 0;

    img {
      max-width: 120px;
      margin-bottom: 24px;
    }

    > span {
      display: block;
      color: $colorN80;
      font-size: 16px;
      letter-spacing: -0.05px;
      line-height: 20px;

      &:first-of-type {
        margin-bottom: 12px;
        font-weight: 700;
      }

      &:last-of-type {
        margin-bottom: 16px;
        font-weight: 600;
      }
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.actions-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 16px;
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: separate;

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 50px;

      th {
        position: sticky !important;
        z-index: 2 !important;
        top: 0;
        padding: 14px 8px 14px 12px;
        border-bottom: 2px solid $colorN30;
        background-color: $colorBe30;
        color: $colorN80;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.05px;
        line-height: 18px;
        text-align: left;
        vertical-align: middle;

        .checkbox {
          display: flex;
          justify-content: center;
        }

        &.pinned {
          z-index: 3 !important;
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        height: 90px;
        background-color: $colorN00;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          /* stylelint-disable-next-line no-descending-specificity -- Generic layout rule for table cells */
          span,
          label {
            display: block;
            margin: 0 12px;
          }

          .product-info {
            min-width: 244px;
            padding: 0 12px;
            margin: 0;
          }

          .popover-wrapper {
            padding: 0 12px;
          }

          .button-wrapper,
          .status-wrapper {
            margin: 0 12px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &::v-deep label {
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          color: $colorN80;
          font-size: 13px;
          letter-spacing: -0.05px;
          -webkit-line-clamp: 3;
          line-height: 18px;
          word-break: break-word;
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN30;
        }
      }

      &.selected {
        td {
          background-color: $colorM05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 2px solid $colorN30;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}

.pagination {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;

  .per-page-wrapper {
    position: relative;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;

    .per-page-text {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      white-space: nowrap;
    }
  }

  &.bottom-pagination {
    margin-top: 24px;
    gap: 24px;
  }
}

.advanced-table thead th .buybox-head {
  display: inline-flex;
  align-items: center;          
}

.advanced-table thead th .buybox-head::before {
  content: "";
  display: inline-block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none' xmlns:v='https://vecta.io/nano'%3E%3Cg fill='%23f4e'%3E%3Cuse href='%23B'/%3E%3Cuse href='%23C'/%3E%3C/g%3E%3Cuse href='%23D' fill='%23fff'/%3E%3Cuse href='%23E' fill-rule='evenodd' fill='%23cc36be'/%3E%3Cg opacity='.1'%3E%3Cuse href='%23B' fill='%233b475e'/%3E%3C/g%3E%3Cuse href='%23C' fill='%23f4e'/%3E%3Cuse href='%23D' fill='%23fff'/%3E%3Cuse href='%23E' fill-rule='evenodd' fill='%23f4e'/%3E%3Cdefs %3E%3Cpath id='B' d='M15.2 13a.8.8 0 0 1 .795.707L16 13.8v7.6a.8.8 0 0 1-1 .775l-.088-.028-2.97-1.944-2.845 1.94a.8.8 0 0 1-1.092-.65L8 21.4v-7.6a.8.8 0 0 1 .8-.8l6.307.005L15.2 13z'/%3E%3Cpath id='C' d='M12 2a7 7 0 1 0 0 14 7 7 0 1 0 0-14z'/%3E%3Cpath id='D' d='M12 3.75a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 1 0 0-10.5z'/%3E%3Cpath id='E' d='M12 10.75l-2.057 1.082.393-2.291-1.664-1.622 2.3-.334L12 5.5l1.029 2.084 2.3.334-1.664 1.622.393 2.291L12 10.75z'/%3E%3C/defs%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
</style>
